import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { raciApi, peopleApi, departmentsApi, linkedArtefactsApi } from '../services/api';
import type { Person } from '../types/models';
import { useToast } from '../hooks/useToast';
import { NimbusTable } from './NimbusTable';
import { NimbusModal, NimbusModalContent } from './NimbusModal';
import { reactSelectStyles } from '../utils/reactSelectStyles';

type RaciRole = 'R' | 'A' | 'C' | 'I';

interface RaciAssignment {
  raciId: string;
  personId: string;
  role: RaciRole;
  notes?: string;
  [key: string]: any; // For dynamic artefactType field (riskId, issueId, etc.)
}

interface Props {
  artefactId: string;
  artefactType: string; // 'risk', 'issue', 'incident', etc.
  compact?: boolean; // Compact mode for sidebar display
  readOnly?: boolean;
}

export const RaciAssignments: React.FC<Props> = ({ artefactId, artefactType, compact = false, readOnly = false }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<RaciAssignment | null>(null);
  const [deleteId, setDeleteId] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [showNewPersonForm, setShowNewPersonForm] = useState(false);
  const [newPersonForm, setNewPersonForm] = useState({ firstName: '', lastName: '', email: '', jobTitle: '', departmentId: '' });

  const handleSyncFromControls = async () => {
    if (!artefactId) return;
    setIsSyncing(true);
    try {
      const linksData = await linkedArtefactsApi.getLinks(artefactId, artefactType);
      const links: any[] = linksData?.links || [];
      const controlLinks = links.filter((l: any) => l.artefact_type === 'control');

      if (controlLinks.length === 0) {
        toast.warning('No linked controls found to sync RACI from.');
        return;
      }

      const controlRaciResults = await Promise.all(
        controlLinks.map((l: any) => raciApi.getByArtefact('control', l.artefact_id))
      );

      const seenKeys = new Set<string>();
      const toCreate: Array<{ artefactType: string; artefactId: string; personId: string; role: string }> = [];
      for (const raciList of controlRaciResults) {
        for (const r of raciList) {
          const k = `${r.personId}:${r.role}`;
          if (!seenKeys.has(k)) {
            seenKeys.add(k);
            toCreate.push({ artefactType: 'evidence', artefactId, personId: r.personId, role: r.role });
          }
        }
      }

      if (toCreate.length === 0) {
        toast.info('Linked controls have no RACI assignments to sync.');
        return;
      }

      const result = await raciApi.batchCreate(toCreate);
      queryClient.invalidateQueries({ queryKey: ['raci', artefactType, artefactId] });
      const { created, skipped } = result as any;
      if (created === 0) {
        toast.info('All RACI already up to date.');
      } else {
        toast.success(`Synced ${created} assignment${created !== 1 ? 's' : ''} from linked controls${skipped > 0 ? ` (${skipped} already existed)` : ''}.`);
      }
    } catch {
      toast.error('Failed to sync RACI from controls.');
    } finally {
      setIsSyncing(false);
    }
  };
  
  const [formData, setFormData] = useState({
    personId: '',
    role: 'R' as RaciRole,
    notes: '',
  });

  const { data: assignments = [], isLoading } = useQuery<RaciAssignment[]>({
    queryKey: ['raci', artefactType, artefactId],
    queryFn: () => raciApi.getByArtefact(artefactType, artefactId),
    enabled: !!artefactId,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  const { data: departments = [] } = useQuery<{ departmentId: string; name: string }[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => raciApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raci', artefactType, artefactId] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ raciId, data }: { raciId: string; data: any }) => raciApi.update(raciId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raci', artefactType, artefactId] });
      setShowModal(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (raciId: string) => raciApi.delete(raciId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raci', artefactType, artefactId] });
      setShowDeleteModal(false);
    },
  });

  const resetForm = () => {
    setFormData({ personId: '', role: 'R', notes: '' });
    setEditingAssignment(null);
    setShowNewPersonForm(false);
    setNewPersonForm({ firstName: '', lastName: '', email: '', jobTitle: '', departmentId: '' });
  };

  const createPersonMutation = useMutation({
    mutationFn: (data: any) => peopleApi.create(data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      const newId = response?.personId || response?.person?.personId;
      if (newId) setFormData((prev) => ({ ...prev, personId: newId }));
      setShowNewPersonForm(false);
      setNewPersonForm({ firstName: '', lastName: '', email: '', jobTitle: '', departmentId: '' });
      toast.success('Person added.');
    },
    onError: () => toast.error('Failed to add person.'),
  });

  const handleCreatePerson = () => {
    if (!newPersonForm.firstName.trim()) return;
    createPersonMutation.mutate({
      firstName: newPersonForm.firstName.trim(),
      lastName: newPersonForm.lastName.trim(),
      email: newPersonForm.email.trim(),
      jobTitle: newPersonForm.jobTitle.trim(),
      departmentId: newPersonForm.departmentId || null,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (assignment: RaciAssignment) => {
    setEditingAssignment(assignment);
    setFormData({
      personId: assignment.personId,
      role: assignment.role,
      notes: assignment.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (!formData.personId) {
      toast.warning('Please select a person');
      return;
    }

    const payload = {
      [`${artefactType}Id`]: artefactId,
      personId: formData.personId,
      role: formData.role,
      notes: formData.notes,
    };

    if (editingAssignment) {
      updateMutation.mutate({ raciId: editingAssignment.raciId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const askDelete = (raciId: string) => {
    setDeleteId(raciId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const getRoleColor = (role: RaciRole): string => {
    const colors: Record<RaciRole, string> = {
      R: 'primary',
      A: 'success',
      C: 'info',
      I: 'warning',
    };
    return colors[role] || 'secondary';
  };

  const getRoleLabel = (role: RaciRole): string => {
    const labels: Record<RaciRole, string> = {
      R: 'Responsible',
      A: 'Accountable',
      C: 'Consulted',
      I: 'Informed',
    };
    return labels[role] || '';
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const getPersonName = (personId: string): string => {
    const person = people.find((p) => p.personId === personId);
    if (!person) return personId;
    
    const firstName = (person as any).firstName || '';
    const lastName = (person as any).lastName || '';
    const full = `${firstName} ${lastName}`.trim();
    
    return full || person.personName || personId;
  };

  const personOptions = useMemo(() =>
    people
      .map((p) => ({ value: p.personId, label: getPersonName(p.personId) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [people]);

  const getPersonInitials = (personId: string): string => {
    const person = people.find((p) => p.personId === personId);
    if (!person) return '?';
    
    const name = getPersonName(personId);
    return name !== personId ? getInitials(name) : '?';
  };

  return (
    <>
      {compact ? (
        /* Compact Sidebar Version */
        <>
          {!readOnly && (
            <button className="np-btn np-btn-primary np-btn-sm w-100 mb-3" onClick={openCreateModal}>
              <i className="ri-add-line"></i>
              Add Assignment
            </button>
          )}

          {isLoading ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-3">
              <i className="ri-shield-user-line" style={{ fontSize: '32px', color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub small mb-0 mt-2">No RACI assignments yet</p>
            </div>
          ) : (
            <div className="np-table-wrap">
              <table className="np-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th style={{ width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment) => (
                    <tr key={assignment.raciId}>
                      <td>{getPersonName(assignment.personId)}</td>
                      <td>
                        <span className={`np-chip np-tone-${getRoleColor(assignment.role)}`}>
                          {assignment.role}
                        </span>
                      </td>
                      <td>
                        {!readOnly && (
                          <div className="d-flex gap-1">
                            <button
                              className="np-icon-btn"
                              onClick={() => openEditModal(assignment)}
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              className="np-icon-btn np-icon-btn-danger"
                              onClick={() => askDelete(assignment.raciId)}
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        /* Full Card Version */
        <div className="col-12 mb-4">
          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-team-line"></i> RACI Assignments</h2>
              {!readOnly && artefactType === 'evidence' && (
                <button
                  className="np-btn np-btn-ghost np-btn-sm"
                  onClick={handleSyncFromControls}
                  disabled={isSyncing}
                  title="Copy RACI from all linked controls (union, no duplicates)"
                >
                  {isSyncing ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <i className="ri-loop-right-line"></i>
                  )}
                  Sync from Controls
                </button>
              )}
              {!readOnly && (
                <button className="np-btn np-btn-primary np-btn-sm" onClick={openCreateModal}>
                  <i className="ri-add-line"></i>
                  Add Assignment
                </button>
              )}
            </div>
            <div className="np-card-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : assignments.length === 0 ? (
                <div className="text-center py-4">
                  <i className="ri-shield-user-line" style={{ fontSize: '48px', color: 'var(--bs-secondary-color)' }}></i>
                  <p className="np-sub mt-2 mb-0">No RACI assignments yet. Add assignments to define responsibilities.</p>
                </div>
              ) : (
                <NimbusTable thead={<tr>
                        <th>Person</th>
                        <th>Role</th>
                        <th>Notes</th>
                        <th style={{ width: '100px' }}>Actions</th>
                      </tr>}>
                      {assignments.map((assignment) => (
                        <tr key={assignment.raciId}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar avatar-xs me-2">
                                <span className="avatar-initial rounded-circle bg-label-primary">
                                  {getPersonInitials(assignment.personId)}
                                </span>
                              </div>
                              {getPersonName(assignment.personId)}
                            </div>
                          </td>
                          <td>
                            <span className={`np-chip np-tone-${getRoleColor(assignment.role)} me-2`}>
                              {assignment.role}
                            </span>
                            {getRoleLabel(assignment.role)}
                          </td>
                          <td>{assignment.notes || '-'}</td>
                          <td>
                            {!readOnly && (
                              <div className="d-flex gap-1">
                                <button
                                  className="np-icon-btn"
                                  onClick={() => openEditModal(assignment)}
                                  title="Edit"
                                >
                                  <i className="ri-edit-line"></i>
                                </button>
                                <button
                                  className="np-icon-btn np-icon-btn-danger"
                                  onClick={() => askDelete(assignment.raciId)}
                                  title="Delete"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                </NimbusTable>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <NimbusModal>
          <NimbusModalContent
            title={editingAssignment ? 'Edit RACI Assignment' : 'Add RACI Assignment'}
            onClose={() => setShowModal(false)}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            }
          >
                <div className="np-field">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="mb-0">
                      Person <span className="text-danger">*</span>
                    </label>
                    <button
                      type="button"
                      className="np-btn np-btn-ghost np-btn-sm"
                      onClick={() => setShowNewPersonForm((v) => !v)}
                    >
                      <i className="ri-user-add-line"></i>New Person
                    </button>
                  </div>
                  <Select
                    options={personOptions}
                    value={personOptions.find((o) => o.value === formData.personId) ?? null}
                    onChange={(opt) => setFormData({ ...formData, personId: opt?.value || '' })}
                    placeholder="Search for a person…"
                    isClearable
                    styles={reactSelectStyles()}
                  />
                  {showNewPersonForm && (
                    <div className="np-card mt-2">
                      <div className="np-card-body">
                        <p className="small fw-semibold mb-2"><i className="ri-user-add-line me-1"></i>Add New Person</p>
                        <div className="row g-2">
                          <div className="col-6 np-field">
                            <label>First Name <span className="text-danger">*</span></label>
                            <input
                              className="np-input"
                              value={newPersonForm.firstName}
                              onChange={(e) => setNewPersonForm((f) => ({ ...f, firstName: e.target.value }))}
                              placeholder="First name"
                            />
                          </div>
                          <div className="col-6 np-field">
                            <label>Last Name</label>
                            <input
                              className="np-input"
                              value={newPersonForm.lastName}
                              onChange={(e) => setNewPersonForm((f) => ({ ...f, lastName: e.target.value }))}
                              placeholder="Last name"
                            />
                          </div>
                          <div className="col-6 np-field">
                            <label>Email</label>
                            <input
                              type="email"
                              className="np-input"
                              value={newPersonForm.email}
                              onChange={(e) => setNewPersonForm((f) => ({ ...f, email: e.target.value }))}
                              placeholder="email@example.com"
                            />
                          </div>
                          <div className="col-6 np-field">
                            <label>Job Title</label>
                            <input
                              className="np-input"
                              value={newPersonForm.jobTitle}
                              onChange={(e) => setNewPersonForm((f) => ({ ...f, jobTitle: e.target.value }))}
                              placeholder="e.g. IT Manager"
                            />
                          </div>
                          <div className="col-12 np-field np-mb-0">
                            <label>Department</label>
                            <select
                              className="np-select"
                              value={newPersonForm.departmentId}
                              onChange={(e) => setNewPersonForm((f) => ({ ...f, departmentId: e.target.value }))}
                            >
                              <option value="">— Select department —</option>
                              {departments.map((d) => (
                                <option key={d.departmentId} value={d.departmentId}>{d.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="np-btn np-btn-primary np-btn-sm"
                            onClick={handleCreatePerson}
                            disabled={createPersonMutation.isPending || !newPersonForm.firstName.trim()}
                          >
                            {createPersonMutation.isPending ? 'Adding…' : 'Add Person'}
                          </button>
                          <button
                            type="button"
                            className="np-btn np-btn-ghost np-btn-sm"
                            onClick={() => setShowNewPersonForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="np-field">
                  <label>
                    Role <span className="text-danger">*</span>
                  </label>
                  <div>
                    {(['R', 'A', 'C', 'I'] as RaciRole[]).map((role) => (
                      <div className="form-check form-check-inline" key={role}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="role"
                          id={`role${role}`}
                          value={role}
                          checked={formData.role === role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value as RaciRole })}
                        />
                        <label className="form-check-label" htmlFor={`role${role}`}>
                          <span className={`np-chip np-tone-${getRoleColor(role)} me-1`}>{role}</span>
                          {getRoleLabel(role)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="np-field np-mb-0">
                  <label>Notes</label>
                  <textarea
                    className="np-textarea"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes about this assignment..."
                  />
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <NimbusModal size="sm">
          <NimbusModalContent
            title="Confirm Delete"
            onClose={() => setShowDeleteModal(false)}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-danger"
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </>
            }
          >
                <p>Are you sure you want to delete this RACI assignment?</p>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </>
  );
};
